import { LitElement } from "lit";
import { getVersionedApiUrl } from '../config/api-config.js';
import { connect } from "../../state/connect-mixin.js";
import store from "../../state/index.js";

export class StateView extends connect(store)(LitElement) {
  static get properties() {
    return {
      // User & Authentication
      orgId: { type: String },
      user: { type: Object },
      authenticated: { type: Boolean },
      sessionToken: { type: String },
      currentMemberProfile: { type: Object },
      // Global permissions snapshot and readiness
      effectivePermissions: { type: Object },
      permissionsReady: { type: Boolean },

      // Knowledge Base (Document Management)
      documents: { type: Array },
      documentContext: { type: Object },
      documentHistory: { type: Array },
      
      // Graph Management
      graphNodes: { type: Array },
      graphEdges: { type: Array },
      selectedNode: { type: Object },
      selectedEdge: { type: Object },
      graphLoading: { type: Boolean },
      graphError: { type: Object },
      graphNodeTypes: { type: Array },
      graphEdgeTypes: { type: Array },
      graphDateRange: { type: Object },
      graphLayoutName: { type: String },
      graphLayoutOptions: { type: Object },
      graphTotalNodes: { type: Number },
      graphTotalEdges: { type: Number },
      graphLastUpdated: { type: Object },

      // Chat & Interaction
      conversations: { type: Array },
      activeConversation: { type: Object },
      messages: { type: Array },
      chatModel: { type: String },
      chatInstructions: { type: String },

      // Integration & Processing
      integrations: { type: Object },
      processingQueue: { type: Array },
      processingStatus: { type: Object },
      currentBatch: { type: Object },

      // UI State
      chatOpen: { type: Boolean },
      sidebarOpen: { type: Boolean },
      activeView: { type: String },
      chatWindowOpen: { type: Boolean },
      showNotification: { type: Boolean },
      menuOpen: { type: Boolean },
      mobileMenuOpen: { type: Boolean },
      loading: { type: Boolean },
      error: { type: Object },
      notifications: { type: Array },

      // Subscription
      subscriptionPlans: { type: Array }
    };
  }

  createRenderRoot() {
    return this;
  }

  dispatch(action, payload) {
    store.dispatch(action, payload);
  }

  setState(payload) {
    return this.dispatch("setState", payload);
  }

  async refreshAccessToken() {
    try {
      const sessionToken = this.user?.sessionToken || localStorage.getItem('sessionToken');
      if (!sessionToken) {
        throw new Error('No session token found');
      }

      const response = await fetch(`${getVersionedApiUrl('v1')}/auth/webauthn/verify-authentication`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: this.user?.id,
          credential: sessionToken,
          orgId: this.user?.orgId
        })
      });

      if (!response.ok) {
        throw new Error('Session validation failed');
      }

      const data = await response.json();
      
      localStorage.setItem('sessionToken', data.sessionToken || data.accessToken);
      
      this.setState({ 
        user: { 
          ...this.user, 
          ...data.user,
          sessionToken: data.sessionToken || data.accessToken,
        },
        authenticated: true,
        sessionToken: data.sessionToken || data.accessToken
      });
      
      return data.accessToken;
    } catch (error) {
      console.error('Error refreshing token:', error);
      localStorage.removeItem('sessionToken');
      this.setState({ 
        user: null,
        authenticated: false,
        sessionToken: null
      });
      throw error;
    }
  }

  handleClose() {
    this.setState({ showNotification: false });
  }

  stateChanged(state) {
    // User & Auth
    this.orgId = state.orgId;
    this.user = state.user;
    this.authenticated = state.authenticated;
    this.sessionToken = state.sessionToken;
    this.currentMemberProfile = state.currentMemberProfile;
    this.effectivePermissions = state.effectivePermissions;
    this.permissionsReady = state.permissionsReady;
    
    // Knowledge Base
    this.documents = state.documents || [];
    this.documentContext = state.documentContext;
    this.documentHistory = state.documentHistory || [];
    
    // Graph Management
    this.graphNodes = state.graphNodes || [];
    this.graphEdges = state.graphEdges || [];
    this.selectedNode = state.selectedNode;
    this.selectedEdge = state.selectedEdge;
    this.graphLoading = state.graphLoading;
    this.graphError = state.graphError;
    this.graphNodeTypes = state.graphNodeTypes || [];
    this.graphEdgeTypes = state.graphEdgeTypes || [];
    this.graphDateRange = state.graphDateRange;
    this.graphLayoutName = state.graphLayoutName;
    this.graphLayoutOptions = state.graphLayoutOptions;
    this.graphTotalNodes = state.graphTotalNodes;
    this.graphTotalEdges = state.graphTotalEdges;
    this.graphLastUpdated = state.graphLastUpdated;

    // Chat & Interaction
    this.conversations = state.conversations || [];
    this.activeConversation = state.activeConversation;
    this.messages = state.messages || [];
    this.chatModel = state.chatModel;
    this.chatInstructions = state.chatInstructions;

    // Integrations & Processing
    this.integrations = state.integrations;
    this.processingQueue = state.processingQueue || [];
    this.processingStatus = state.processingStatus;
    this.currentBatch = state.currentBatch;

    // UI State
    this.loading = state.loading;
    this.error = state.error;
    this.sidebarOpen = state.sidebarOpen;
    this.activeView = state.activeView;
    this.chatWindowOpen = state.chatWindowOpen;
    this.showNotification = state.showNotification;
    this.chatOpen = state.chatOpen;
    this.menuOpen = state.menuOpen;
    this.mobileMenuOpen = state.mobileMenuOpen;
    this.notifications = state.notifications || [];
    
    // Subscription
    this.subscriptionPlans = state.subscriptionPlans;
  }
}
